import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import FormField from '../../core/components/FormField.tsx';
import SaveStatus from '../../core/components/SaveStatus.tsx';
import countFieldErrors from '../../core/utils/countFieldErrors.ts';
import StringField from '../../core/components/Fields/StringField.tsx';
import { useTranslation } from 'react-i18next';
import SelectField from '../../core/components/Fields/SelectField.tsx';
import Button from '../../core/components/Button.tsx';
import Page from '../../core/components/Page';
import Layout from '../../core/components/Layout.tsx';
import { useMutation, useQuery } from '@apollo/client';
import { DELETE_CUSTOMER_LOGO, GET_COMPANY_DETAILS, UPDATE_CUSTOMER, UPLOAD_CUSTOMER_LOGO } from '../queries.ts';
import { useToaster } from '../../core/components/Toast.tsx';
import Checkbox from '../../core/components/Checkbox.tsx';
import SettingsFormCard from '../../core/components/SettingsFormCard.tsx';
import { COUNTRY_CODES } from '../../core/utils';
import SettingsNavigation from '../../core/components/SettingsNavigation.tsx';
import { ForceSelectionAutoCompleteField } from '../../core/components/Fields/AutoCompleteField.tsx';
import DropArea from '../../core/components/DropArea.tsx';

const CUSTOMER_PROFILE_FORM_DEFAULT_VALUES = {
    name: '',
    invoiceEmail: '',
    streetAddress: '',
    postalCode: '',
    city: '',
    countryCode: '',
    billingStreetAddress: '',
    billingPostalCode: '',
    billingCity: '',
    billingCountryCode: '',
    timezone: '',
};

const CustomerProfilePage = () => {
    const { t } = useTranslation('customer');
    const { publishToast } = useToaster();

    const form = useForm({ defaultValues: CUSTOMER_PROFILE_FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    const { data } = useQuery(GET_COMPANY_DETAILS, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
        variables: {},
    });
    const customer = data?.viewer?.customer;
    const timezoneOptions = React.useMemo(
        () =>
            customer?.timezoneOptions?.map((tz) => ({
                label: tz.replace(/_/g, ' '),
                value: tz.replace(/_/g, ' '),
            })) || [],
        [customer?.timezoneOptions]
    );

    useEffect(() => {
        if (customer) {
            // convert timezone value to label for display
            const timezoneLabel = customer.timezone ? customer.timezone.replace(/_/g, ' ') : '';

            form.reset({
                ...CUSTOMER_PROFILE_FORM_DEFAULT_VALUES,
                name: customer.name,
                invoiceEmail: customer.invoiceEmail,
                streetAddress: customer.streetAddress,
                postalCode: customer.postalCode,
                city: customer.city,
                timezone: timezoneLabel,
                countryCode: customer.countryCode,
                billingStreetAddress: isBillingSameAsShipping ? '' : customer.billingStreetAddress,
                billingPostalCode: isBillingSameAsShipping ? '' : customer.billingPostalCode,
                billingCity: isBillingSameAsShipping ? '' : customer.billingCity,
                billingCountryCode: isBillingSameAsShipping ? '' : customer.billingCountryCode,
            });
            setIsBillingSameAsShipping(customer.isBillingSameAsShipping);
            setLogo(customer.logoUrl || null);
        }
    }, [customer]);

    const [isBillingSameAsShipping, setIsBillingSameAsShipping] = useState(customer?.isBillingSameAsShipping);
    const [logo, setLogo] = useState<string | File | null>(customer?.logoUrl || null);

    const [uploadLogo] = useMutation(UPLOAD_CUSTOMER_LOGO, {
        refetchQueries: [{ query: GET_COMPANY_DETAILS }],
    });
    const [deleteLogo] = useMutation(DELETE_CUSTOMER_LOGO, {
        refetchQueries: [{ query: GET_COMPANY_DETAILS }],
    });

    const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
    useEffect(() => {
        if (!(logo instanceof File)) {
            setLogoPreviewUrl(null);
            return;
        }
        const url = URL.createObjectURL(logo);
        setLogoPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [logo]);

    const [updateCustomer] = useMutation(UPDATE_CUSTOMER);
    const handleUpdateCustomer = async (formData) => {
        try {
            if (logo === null && customer?.logoUrl) {
                await deleteLogo();
            }
            if (logo instanceof File) {
                await uploadLogo({ variables: { file: logo } });
            }

            const timezoneValue = formData.timezone ? formData.timezone.replace(/ /g, '_') : '';
            await updateCustomer({ variables: { input: { ...formData, timezone: timezoneValue } } });
            publishToast({ description: t('profile.form.updateSuccess'), status: 'success' });
            form.reset(formData);
        } catch (e) {
            console.error(e);
            publishToast({ description: t('profile.form.updateError'), status: 'error' });
        }
    };

    const showLogoPreview = logo !== null;

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <Page.HeaderTitle>{t('profile.title')}</Page.HeaderTitle>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6 relative">
                    <SettingsFormCard onSubmit={form.handleSubmit(handleUpdateCustomer)}>
                        <FormField label={t('profile.form.logo')}>
                            {showLogoPreview ? (
                                <div className="flex items-center gap-4">
                                    <img
                                        src={logoPreviewUrl || (typeof logo === 'string' ? logo : undefined)}
                                        alt="Company logo"
                                        className="h-16 max-w-48 object-contain rounded border border-line-weak p-1"
                                    />
                                    <Button
                                        variant="ghost"
                                        type="button"
                                        onClick={() => setLogo(null)}
                                        className="text-content-danger-strong"
                                    >
                                        {t('profile.form.logoRemoveButton')}
                                    </Button>
                                </div>
                            ) : (
                                <DropArea
                                    placeholder={t('profile.form.logoUploadPlaceholder')}
                                    maxFiles={1}
                                    accept={{
                                        'image/png': ['.png'],
                                        'image/jpeg': ['.jpeg', '.jpg'],
                                        'image/svg+xml': ['.svg'],
                                    }}
                                    onFilesChange={(files) => setLogo(files[0] || null)}
                                />
                            )}
                        </FormField>

                        <Controller
                            name="name"
                            control={form.control}
                            rules={{ required: t('profile.form.nameRequired') }}
                            render={({ field }) => (
                                <FormField label={t('profile.form.name')} required={true} error={formErrors.name}>
                                    <StringField required={true} {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="invoiceEmail"
                            control={form.control}
                            rules={{ required: t('profile.form.invoiceEmailRequired') }}
                            render={({ field }) => (
                                <FormField
                                    label={t('profile.form.invoiceEmail')}
                                    required={true}
                                    error={formErrors.invoiceEmail}
                                >
                                    <StringField required={true} {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="streetAddress"
                            control={form.control}
                            rules={{ required: t('profile.form.streetAddressRequired') }}
                            render={({ field }) => (
                                <FormField
                                    label={t('profile.form.streetAddress')}
                                    required={true}
                                    error={formErrors.streetAddress}
                                >
                                    <StringField required={true} {...field} />
                                </FormField>
                            )}
                        />

                        <div className="flex flex-col gap-4 md:flex-row">
                            <Controller
                                name="postalCode"
                                control={form.control}
                                rules={{ required: t('profile.form.postalCodeRequired') }}
                                render={({ field }) => (
                                    <FormField
                                        label={t('profile.form.postalCode')}
                                        required={true}
                                        error={formErrors.postalCode}
                                        className="flex-1"
                                    >
                                        <StringField required={true} {...field} />
                                    </FormField>
                                )}
                            />

                            <Controller
                                name="city"
                                control={form.control}
                                rules={{ required: t('profile.form.cityRequired') }}
                                render={({ field }) => (
                                    <FormField
                                        label={t('profile.form.city')}
                                        required={true}
                                        error={formErrors.city}
                                        className="flex-1"
                                    >
                                        <StringField required={true} {...field} />
                                    </FormField>
                                )}
                            />

                            <Controller
                                name="countryCode"
                                control={form.control}
                                rules={{ required: t('profile.form.countryCodeRequired') }}
                                render={({ field }) => (
                                    <FormField
                                        label={t('profile.form.countryCode')}
                                        required={true}
                                        error={formErrors.countryCode}
                                        className="flex-1"
                                    >
                                        <SelectField
                                            options={COUNTRY_CODES.map((code) => ({ label: code, value: code }))}
                                            required={true}
                                            {...field}
                                        />
                                    </FormField>
                                )}
                            />
                        </div>

                        <Controller
                            name="timezone"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('profile.form.timezone')}
                                    required={true}
                                    error={formErrors.timezone}
                                    className="flex-1"
                                >
                                    <ForceSelectionAutoCompleteField
                                        options={timezoneOptions}
                                        placeholder={t('profile.form.timezonePlaceholder')}
                                        value={field.value}
                                        onValueChange={field.onChange}
                                        onBlur={field.onBlur}
                                        onFocus={() => {}}
                                    />
                                </FormField>
                            )}
                        />

                        <label className="flex items-center">
                            <Checkbox
                                checked={isBillingSameAsShipping}
                                onChange={(e) => setIsBillingSameAsShipping(e.target.checked)}
                                className="mr-2"
                            />
                            <span className="text-xs text-content-medium min-w-36 flex-none flex justify-start gap-1">
                                {t('profile.form.isBillingSameAsShipping')}
                            </span>
                        </label>

                        {!isBillingSameAsShipping && (
                            <>
                                <Controller
                                    name="billingStreetAddress"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormField
                                            label={t('profile.form.billingStreetAddress')}
                                            error={formErrors.billingStreetAddress}
                                        >
                                            <StringField {...field} />
                                        </FormField>
                                    )}
                                />

                                <div className="flex flex-col gap-4 md:flex-row">
                                    <Controller
                                        name="billingPostalCode"
                                        control={form.control}
                                        render={({ field }) => (
                                            <FormField
                                                label={t('profile.form.billingPostalCode')}
                                                error={formErrors.billingPostalCode}
                                                className="flex-1"
                                            >
                                                <StringField {...field} />
                                            </FormField>
                                        )}
                                    />

                                    <Controller
                                        name="billingCity"
                                        control={form.control}
                                        render={({ field }) => (
                                            <FormField
                                                label={t('profile.form.billingCity')}
                                                error={formErrors.billingCity}
                                                className="flex-1"
                                            >
                                                <StringField {...field} />
                                            </FormField>
                                        )}
                                    />

                                    <Controller
                                        name="billingCountryCode"
                                        control={form.control}
                                        render={({ field }) => (
                                            <FormField
                                                label={t('profile.form.billingCountryCode')}
                                                error={formErrors.billingCountryCode}
                                                className="flex-1"
                                            >
                                                <SelectField
                                                    options={COUNTRY_CODES.map((code) => ({
                                                        label: code,
                                                        value: code,
                                                    }))}
                                                    {...field}
                                                />
                                            </FormField>
                                        )}
                                    />
                                </div>
                            </>
                        )}

                        <SaveStatus
                            isDirty={form.formState.isDirty}
                            isSubmitting={form.formState.isSubmitting}
                            errorCount={countFieldErrors(formErrors)}
                        />
                    </SettingsFormCard>
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default CustomerProfilePage;
